// AI 能力面板 - 可复用的 AI 结果弹窗
import { useState, useContext, createContext, useCallback, type ReactNode } from 'react';
import { api } from '../api/client';

// ===== 类型 =====
type AIStatus = 'idle' | 'loading' | 'done' | 'error';

interface AIState {
  status: AIStatus;
  title: string;
  result: string;
  error: string;
  model: string;
}

interface AICtx {
  open: (title: string, loader: (model: string) => Promise<string>) => void;
  close: () => void;
}

const AIPanelContext = createContext<AICtx>({
  open: () => {},
  close: () => {},
});

export function useAI() {
  return useContext(AIPanelContext);
}

export function AIPanelProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AIState>({
    status: 'idle',
    title: '',
    result: '',
    error: '',
    model: 'qwen3.7-plus',
  });

  const open = useCallback((title: string, loader: (model: string) => Promise<string>) => {
    setState({ status: 'loading', title, result: '', error: '', model: 'qwen3.7-plus' });
    loader('qwen3.7-plus')
      .then((result) => setState((s) => ({ ...s, status: 'done', result })))
      .catch((e) => setState((s) => ({ ...s, status: 'error', error: String(e?.message ?? e) })));
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, status: 'idle' }));
  }, []);

  return (
    <AIPanelContext.Provider value={{ open, close }}>
      {children}
      {state.status !== 'idle' && <Panel {...state} onClose={close} />}
    </AIPanelContext.Provider>
  );
}

function Panel({ status, title, result, error, onClose }: AIState & { onClose: () => void }) {
  return (
    <div className="ai-modal-overlay" onClick={onClose}>
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-head">
          <span className="ai-badge">AI</span>
          <span className="ai-modal-title">{title}</span>
          <button className="ai-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ai-modal-body">
          {status === 'loading' && (
            <div className="ai-loading">
              <div className="ai-spinner" />
              <span>AI 正在分析中…</span>
            </div>
          )}
          {status === 'done' && <Markdown text={result} />}
          {status === 'error' && (
            <div className="ai-error">
              <p>请求失败</p>
              <pre>{error}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Simple markdown renderer
function Markdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const out: any[] = [];
  let list: any[] = [];
  let key = 0;
  const flush = () => {
    if (list.length > 0) {
      out.push(<ul key={'ul' + key++} className="ai-md-list">{list}</ul>);
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flush(); continue; }
    if (line.startsWith('### ')) { flush(); out.push(<h4 key={key++} className="ai-md-h3">{line.slice(4)}</h4>); continue; }
    if (line.startsWith('## ')) { flush(); out.push(<h3 key={key++} className="ai-md-h2">{line.slice(3)}</h3>); continue; }
    if (line.startsWith('# ')) { flush(); out.push(<h2 key={key++} className="ai-md-h1">{line.slice(2)}</h2>); continue; }
    const num = line.match(/^(\d+)\.\s+(.*)/);
    if (num) { list.push(<li key={key++} className="ai-md-li">{renderInline(num[2])}</li>); continue; }
    if (line.startsWith('- ') || line.startsWith('* ')) {
      list.push(<li key={key++} className="ai-md-li">{renderInline(line.slice(2))}</li>);
      continue;
    }
    flush();
    out.push(<p key={key++} className="ai-md-p">{renderInline(line)}</p>);
  }
  flush();
  return <div className="ai-md">{out}</div>;
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}
